import React from 'react';
import Button from '@mui/material/Button';
import { dateStrToAgeDays, dateStrToAgeMonths, PatientDobFormat, useGenerateDescriptiveReportMutation } from "@cyren/common-lib";
import { onError } from '../../../utils/apollo-utils';
import { produce } from 'immer'; // immer 라이브러리 사용 가정
import { useStaticLocales } from '../../hooks/locales/use-static-locale';
import { extractDescriptiveSections } from '../../../v2/shared/utils/extractDescriptiveSections';

interface GenerateDescriptiveNoteButtonProps {
  // onClick prop은 더 이상 외부에서 전달받지 않고, 내부 함수를 사용합니다.
  // onClick?: () => void; // 필요 없어짐
  disabled?: boolean;
  reportState: any;
  visit: any;
  // 새로 추가: 생성된 텍스트를 상위 컴포넌트로 전달할 콜백 함수
  onReportGenerated: (generatedText: string | null) => void;
  // setIsGeneratingDesc도 이제 상위 컴포넌트에서 관리하도록 전달
  setIsGeneratingDesc: (isLoading: boolean) => void;
  promptInfoHpi: any[];
  treeStatesInfo: any[];
  isDemo: boolean | false;
}

const GenerateDescriptiveNoteButton: React.FC<GenerateDescriptiveNoteButtonProps> = ({
  disabled = false,
  reportState,
  visit,
  onReportGenerated, // 새로 추가된 콜백 함수
  setIsGeneratingDesc, // 새로 추가된 로딩 상태 콜백 함수
  promptInfoHpi,
  treeStatesInfo,
  isDemo,
}) => {
  const { sltStr } = useStaticLocales();
  const [generateDescriptiveReport, { data: reportData, loading: loadingReport, error: reportError }] = useGenerateDescriptiveReportMutation({
    onError,
  });

  const toJoinedOrNone = (lines: string[]) => {
    const normalized = Array.from(new Set((lines || []).map((line) => String(line || '').trim()).filter(Boolean)));
    return normalized.length > 0 ? normalized.join('\n') : 'None';
  };

  const handleGenerateDescriptiveReport = async () => {
    // 로딩 상태 시작을 부모에게 알림
    setIsGeneratingDesc(true);

    if (!reportState || !visit?.id) {
      setIsGeneratingDesc(false);
      onReportGenerated(null); // 실패 시 null 전달
      return;
    }

    const cc = reportState.systemValueMap?.cc || '';

    const { hpiData, pmhData, allergyData, medicationData } = extractDescriptiveSections(reportState);
    const mergedHpiLines = Array.from(
      new Set(
        [...(promptInfoHpi || []), ...(hpiData || [])]
          .map((line) => String(line || '').trim())
          .filter(Boolean),
      ),
    );
    const pmhText = toJoinedOrNone(pmhData || []);
    const hpiText = toJoinedOrNone(mergedHpiLines);
    const medicationText = toJoinedOrNone(medicationData || []);
    const allergyText = toJoinedOrNone(allergyData || []);
    const pmhChecklist = toJoinedOrNone((pmhData || []).map((line) => `- ${line}`));
    const hpiChecklist = toJoinedOrNone(mergedHpiLines.map((line) => `- ${line}`));
    const medicationChecklist = toJoinedOrNone((medicationData || []).map((line) => `- ${line}`));
    const allergyChecklist = toJoinedOrNone((allergyData || []).map((line) => `- ${line}`));

    const patientInfo = treeStatesInfo.filter((info: any) => info.includes('patient-info')).join('\n');
    let dob = reportState.systemValueMap?.age || ""; // 'age'는 DOB 문자열일 것으로 가정
    if (dob === "" && patientInfo) {
      dob = patientInfo.split('patient-birthday:')[1].split("\n")[0].trim();
    }

    let age: number | null = null;
    // let monthAge: number | null = null;
    const birthDate = new Date(dob);
    const today = new Date();
    const currentYear = today.getFullYear();
    const birthYear = birthDate.getFullYear();
    age = currentYear - birthYear;

    const monthAge = dateStrToAgeMonths(dob, {
      inputFormat: PatientDobFormat,
    });
    const dayAge = dateStrToAgeDays(dob, {
      inputFormat: PatientDobFormat,
    });

    const monthDifference = today.getMonth() - birthDate.getMonth();
    const dayDifference = today.getDate() - birthDate.getDate();
    // 생일이 아직 안 지났으면 나이에서 1을 뺌 (모든 나이에 적용)
    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
        age--;
    }

    if (patientInfo && patientInfo.includes('age')) {
      const calculatedAge = parseInt(patientInfo.split('age:')[1].split("\n")[0].trim(), 10);
      if (calculatedAge !== age) {
        age = calculatedAge;
      }
    }

    let prompt = '';

    if (isDemo) {
      prompt = `
      You are an expert medical scribe specializing in clinical documentation. Your task is to transform the following raw, patient-generated clinical data into a formal History of Present Illness (HPI) note.
  The HPI must be a clear, concise, and chronologically organized narrative written in professional medical prose. Follow these specific principles:
  1. Structure of the HPI:
  - Opening Statement: The first sentence is critical. It must introduce the patient by stating their age, gender, any relevant past medical history, and their chief complaint. If Past Medical History is not provided or empty, do not mention it. If allergy is not provided do not mention it.
    Example: “The patient is a 34-year-old male with no relevant past medical history who presents for evaluation following a motor vehicle collision.”
    However, If the age of the patient is below 2, describe age in months instead of years. In addition, if monthAge is provided, use it instead of age.
  - Narrative Body: After the opening, build a chronological story of the events. Integrate the details of the chief complaint using the OLDCARTS framework as a guide to ensure completeness:
    Onset: When did it start?
    Location: Where is the symptom?
    Duration: How long has it been going on?
    Character: What does the symptom feel like?
    Aggravating/Alleviating Factors: What makes it better or worse?
    Radiation: Does the pain or symptom travel?
    Timing: Is it constant or intermittent?
    Severity: How bad is it?
    Associated Symptoms & Context: Weave in any related details, such as the mechanism of injury, treatments attempted, and pertinent negatives (the absence of key symptoms, like denying head trauma).
  2. MUST WRITE IN THE FOLLOWING ORDER:
    (1) Opening Statement (First sentence)
    (2) Narrative Body (After the opening, build a chronological story of the events. Integrate the details of the chief complaint using the OLDCARTS framework as a guide to ensure completeness)
    (3) Do not write Past Medical History and Medication History in narrative form. If provided, list them at the end as follows, If there is no medication, mention it 'None':
    Example:
    Medication: Metformin, Amlodipine
    (4) Do not write Allergy in narrative form. If provided, list them at the end as follows, If there is no allergy, mention it 'None':
    Example:
    Allergy: Bacmin, Cat'claw
    (5) If there is a language other than English, document it like the following example. Example) The patient is a 44-year-old female who presents with abdominal pain [Auto-translated from Korean 배가 아파요]. for his symptoms 6 hours prior to presentation.
  3. Rules for Content:
  Synthesize, Don’t List: Do not simply list the data points. Weave them into a coherent paragraph.
  No New Information: You must not add any information or make assumptions that are not explicitly provided in the data below.
  Use All Information: You must incorporate every piece of provided data into your narrative.
  
  Avoid Redundancy: Each symptom or detail should be mentioned only once unless it adds new temporal or qualitative information. Do not repeat symptoms with only minor wording changes.
  
  Preserve Logical Flow: Do not imply causal relationships between symptoms unless such relationships are explicitly stated. Avoid suggesting that one symptom caused or worsened another unless that is clearly indicated in the input.
  
  Maintain Chronological and Thematic Clarity: Organize symptoms first chronologically, and secondarily by system (e.g., respiratory, gastrointestinal, genitourinary). If multiple symptoms begin on the same day, group them by system for readability.
  
  Professional Language: Convert abbreviations (like M, F, CC, PMHx) and patient-friendly language into a formal clinical narrative. Use medically appropriate terminology and tone throughout.
  4. Hard Constraints (Non-negotiable):
  - You MUST include every item in the coverage checklists below.
  - If Allergy Coverage Checklist contains any affirmative evidence (e.g., Yes/o-yes or specific entries), NEVER output "Allergy: None/없음".
  - If Medication Coverage Checklist contains any affirmative evidence, NEVER output "Medication: None/없음".
  
  Raw Clinical Data to Process:
  ${reportState.systemValueMap?.sex?.toString().replace("o-", "") || 'Gender is Not provided'} / ${
    reportState.systemValueMap?.dateOfBirth || 'Age is Not provided'
  }
  CC
  ${cc}
  ===
  Past Medical History:
  ${pmhText}
  ===
  HPI:
  ${hpiText}
  ===
  Medication:
  ${medicationText}
  ===
  Allergy:
  ${allergyText}
  ===
  HPI Coverage Checklist (MUST COVER ALL):
  ${hpiChecklist}
  ===
  PMH Coverage Checklist (MUST COVER ALL):
  ${pmhChecklist}
  ===
  Medication Coverage Checklist (MUST COVER ALL):
  ${medicationChecklist}
  ===
  Allergy Coverage Checklist (MUST COVER ALL):
  ${allergyChecklist}
  ===
  Task:
  Now, generate the HPI note based on the data and instructions provided.
  `;
      
    } else {
      prompt = `
      You are an expert medical scribe specializing in clinical documentation. Your task is to transform the following raw, patient-generated clinical data into a formal History of Present Illness (HPI) note.
  The HPI must be a clear, concise, and chronologically organized narrative written in professional medical prose. Follow these specific principles:
  1. Structure of the HPI:
  - Opening Statement: The first sentence is critical. It must introduce the patient by stating their age, gender, any relevant past medical history, and their chief complaint. If Past Medical History is not provided or empty, do not mention it. If allergy is not provided do not mention it.
    Example: “The patient is a 34-year-old male with no relevant past medical history who presents for evaluation following a motor vehicle collision.”
    However, If the age of the patient is below 2, describe age in months instead of years. In addition, if monthAge is provided, use it instead of age.
  - Narrative Body: After the opening, build a chronological story of the events. Integrate the details of the chief complaint using the OLDCARTS framework as a guide to ensure completeness:
    Onset: When did it start?
    Location: Where is the symptom?
    Duration: How long has it been going on?
    Character: What does the symptom feel like?
    Aggravating/Alleviating Factors: What makes it better or worse?
    Radiation: Does the pain or symptom travel?
    Timing: Is it constant or intermittent?
    Severity: How bad is it?
    Associated Symptoms & Context: Weave in any related details, such as the mechanism of injury, treatments attempted, and pertinent negatives (the absence of key symptoms, like denying head trauma).
  2. MUST WRITE IN THE FOLLOWING ORDER:
    (1) Opening Statement (First sentence)
    (2) Narrative Body (After the opening, build a chronological story of the events. Integrate the details of the chief complaint using the OLDCARTS framework as a guide to ensure completeness)
    (3) Do not write Past Medical History and Medication History in narrative form. If provided, list them at the end as follows, If there is no medication, mention it 'None':
    Example:
    Medication: Metformin, Amlodipine
    (4) Do not write Allergy in narrative form. If provided, list them at the end as follows, If there is no allergy, mention it 'None':
    Example:
    Allergy: Bacmin, Cat'claw
    (5) If there is a language other than English, document it like the following example. Example) The patient is a 44-year-old female who presents with abdominal pain [Auto-translated from Korean 배가 아파요]. for his symptoms 6 hours prior to presentation.
  3. Rules for Content:
  Synthesize, Don’t List: Do not simply list the data points. Weave them into a coherent paragraph.
  No New Information: You must not add any information or make assumptions that are not explicitly provided in the data below.
  Use All Information: You must incorporate every piece of provided data into your narrative.
  
  Avoid Redundancy: Each symptom or detail should be mentioned only once unless it adds new temporal or qualitative information. Do not repeat symptoms with only minor wording changes.
  
  Preserve Logical Flow: Do not imply causal relationships between symptoms unless such relationships are explicitly stated. Avoid suggesting that one symptom caused or worsened another unless that is clearly indicated in the input.
  
  Maintain Chronological and Thematic Clarity: Organize symptoms first chronologically, and secondarily by system (e.g., respiratory, gastrointestinal, genitourinary). If multiple symptoms begin on the same day, group them by system for readability.
  
  Professional Language: Convert abbreviations (like M, F, CC, PMHx) and patient-friendly language into a formal clinical narrative. Use medically appropriate terminology and tone throughout.
  4. Hard Constraints (Non-negotiable):
  - You MUST include every item in the coverage checklists below.
  - If Allergy Coverage Checklist contains any affirmative evidence (e.g., Yes/o-yes or specific entries), NEVER output "Allergy: None/없음".
  - If Medication Coverage Checklist contains any affirmative evidence, NEVER output "Medication: None/없음".
  
  Raw Clinical Data to Process:
  ${reportState.systemValueMap?.sex?.toString().replace("o-", "") || 'Gender is Not provided'} / ${
    (() => {
      if (age !== null && age >= 2) {
        return `${age} years old`;
      } 
      if (monthAge !== null && monthAge >= 1) {
        return `${monthAge} months old`;
      }
      if (dayAge !== null && monthAge < 1) {
        return `${dayAge} days old`;
      }
      return 'Age is Not provided';
    })()
  }
  CC
  ${cc}
  ===
  Past Medical History:
  ${pmhText}
  ===
  HPI:
  ${hpiText}
  ===
  Medication:
  ${medicationText}
  ===
  Allergy:
  ${allergyText}
  ===
  HPI Coverage Checklist (MUST COVER ALL):
  ${hpiChecklist}
  ===
  PMH Coverage Checklist (MUST COVER ALL):
  ${pmhChecklist}
  ===
  Medication Coverage Checklist (MUST COVER ALL):
  ${medicationChecklist}
  ===
  Allergy Coverage Checklist (MUST COVER ALL):
  ${allergyChecklist}
  ===
  Task:
  Now, generate the HPI note based on the data and instructions provided.
  `
    }

    const response = await generateDescriptiveReport({
      variables: {
        visitId: visit?.uuid ?? '',
        prompt,
      },
    });
    const newText = response.data?.generateDescriptiveReport?.text || null;

    // 생성된 텍스트를 부모 컴포넌트로 전달
    onReportGenerated(newText);
    
    // 로딩 상태 종료를 부모에게 알림
    setIsGeneratingDesc(false);
  };

  return (
    <Button
      variant="outlined"
      onClick={handleGenerateDescriptiveReport}
      disabled={disabled || loadingReport}
      sx={{
        borderRadius: "0.6rem",
        borderWidth: "1.5px",
        borderColor: "#E6E9F5",
        // 기존 마진 제거 (컨테이너에서 마진/패딩을 제어할 것)
        marginTop: '1rem',
        marginBottom: '1rem',
        marginLeft: '2.5rem',
        marginRight: '2.5rem',
        width: '100%', // <-- 너비를 100%로 설정하여 부모 컨테이너의 너비를 꽉 채우도록
        color: '#222638',
        // 여분의 공간(padding)을 버튼 내부에 직접 적용 (Tailwind px-x와 유사)
        padding: '0.75rem 1rem', // 상하, 좌우 패딩을 조절하여 버튼의 "두께"와 "여백"을 만듭니다.
        // 예를 들어 `py-3 px-4`와 유사하게
      }}
    >
      {loadingReport ? sltStr({ key: 'desc-btn-generating' }) : sltStr({ key: 'desc-btn-generate' })}
    </Button>
  );
};

export default GenerateDescriptiveNoteButton;
